import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { motion } from "framer-motion";

// Importing modal components
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../../modals/AnimatedModal";

// Importing common helpers and validators
import {
  ERROR_REQUIRED_FIELDS,
  STRING_NOT_NULL_HELPER,
} from "../../../common/helpersTxt";
import { validateString } from "../../../common/validators";

// Importing frames and APIs
import { getComplements } from "../../../api/complement";
import { getProductsCategories } from "../../../api/product";
import {
  cfopFrame,
  cofins_situacao_tributaria_frame,
  icms_origem_frame,
  icms_situacao_tributaria_frame,
  pis_situacao_tributaria_frame,
} from "../../../common/frames";

// Importing components
import MoneyTextField from "../../Inputs/MoneyTextField";
import ItemRowWithCheckbox from "../complements/itemRowWithCheckbox";
import CategoryRowWithCheckbox from "../categories/categoryRowWithCheckbox";
import ItemFiscalDataForm from "../fiscal/ItemFiscalDataForm";

// Material UI components
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Radio from "@mui/material/Radio";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import { OutlinedInput, Typography } from "@mui/material";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CategoryIcon from "@mui/icons-material/Category";
import ExtensionIcon from "@mui/icons-material/Extension";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Switch from "@mui/material/Switch";

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
};

const buttonVariants = {
  hover: { scale: 1.05 },
  tap: { scale: 0.95 },
};

// Tab panel component for the tabbed interface
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </Box>
      )}
    </div>
  );
}

export default function ProductModal({
  show,
  closeModal,
  product,
  createProduct,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [tabValue, setTabValue] = useState(0);

  const [isEditing, setIsEditing] = useState(!!product);
  const [available, setAvailable] = useState(product?.available ?? true);

  const [complementTitle, setComplementTitle] = useState("");
  const [complementsList, setComplementsList] = useState([]);
  const [complementsListToDelete, setComplementsListToDelete] = useState([]);

  const [categoriesList, setCategoriesList] = useState([]);
  const [categoriesListToDelete, setCategoriesListToDelete] = useState([]);

  const [submitted, setSubmitted] = useState(false);

  const [title, setTitle] = useState(product?.title || "");
  const [isTitleValid, setIsTitleValid] = useState(false);

  // Format the initial price value for MoneyTextField
  const formatInitialPrice = (priceValue) => {
    if (!priceValue) return "";
    if (typeof priceValue === "number") {
      return priceValue.toFixed(2).replace(".", ",");
    }
    return priceValue.toString().replace(".", ",");
  };

  const [price, setPrice] = useState(formatInitialPrice(product?.price || ""));
  const [fiscal_code, setFiscalCode] = useState(product?.fiscal_code || "");
  const [ifoodExternalCode, setIfoodExternalCode] = useState(
    product?.ifood_pos_code || ""
  );
  const [cfop_code, setCfopCode] = useState(cfopFrame[0]);
  const [icmsSituacaoTributariaState, setIcmsSituacaoTributariaState] =
    useState(icms_situacao_tributaria_frame[0]);
  const [icmsOrigemState, setIcmsOrigemState] = useState(icms_origem_frame[0]);
  const [icmsValue, setIcmsValue] = useState(0);

  const [cofinsSituacaoTributariaState, setCofinsSituacaoTributariaState] =
    useState(cofins_situacao_tributaria_frame[0]);
  const [cofinsValue, setCofinsValue] = useState(0);

  const [pisSituacaoTributariaState, setPisSituacaoTributariaState] = useState(
    pis_situacao_tributaria_frame[0]
  );
  const [pisValue, setPisValue] = useState(0);

  const [unit, setUnit] = useState(product?.unit || "UN");

  const [width, setWidth] = useState(product?.width || 0);
  const [length, setLength] = useState(product?.length || 0);
  const [height, setHeight] = useState(product?.height || 0);
  const [weight, setWeight] = useState(product?.weight || 0);

  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState(ERROR_REQUIRED_FIELDS);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Convert price from formatted string to float
  const convertPriceToFloat = (formattedPrice) => {
    if (!formattedPrice) return 0;

    // Remove R$ and any non-numeric characters except comma
    const cleanedPrice = formattedPrice
      .replace(/R\$\s*/g, "")
      .replace(/\./g, "")
      .trim();

    // Replace comma with dot for parsing
    const numericPrice = cleanedPrice.replace(",", ".");

    return parseFloat(numericPrice || 0);
  };

  useEffect(() => {
    setTimeout(async () => {
      await loadData();
    }, 10);
  }, []);

  const fetchProductComplements = async (product_id) => {
    const params = { pid: product_id };
    const { data } = await getComplements(params);

    return data.map((complement) => ({
      ...complement,
      existed: true,
      checked: true,
    }));
  };

  const fetchProductCategories = async (product_id) => {
    const params = { product_id: product_id };
    const { data } = await getProductsCategories(params);

    return data.map((category) => ({
      ...category,
      existed: true,
      checked: true,
    }));
  };

  const fetchComplementsList = async (params) => {
    const { data } = await getComplements(params);
    return data;
  };

  const fetchCategoriesList = async () => {
    const { data } = await getProductsCategories();
    return data;
  };

  const loadData = async () => {
    const params = { page: page };

    let treatedProductComplements = [];
    let treatedProductCategories = [];

    if (product) {
      setFiscalStates(product);

      const { product_id } = product;

      treatedProductComplements = await fetchProductComplements(product_id);
      treatedProductCategories = await fetchProductCategories(product_id);
      setIsEditing(true);
    }

    const complementData = await fetchComplementsList(params);
    const newDataComplements = complementData.filter(
      (item) =>
        !treatedProductComplements.some(
          (elem) => elem.complement === item.complement_id
        )
    );
    setComplementsList([...treatedProductComplements, ...newDataComplements]);

    const categoryData = await fetchCategoriesList();
    const newCategoryData = categoryData.filter(
      (item) => !treatedProductCategories.some((elem) => elem.id === item.id)
    );
    setCategoriesList([...treatedProductCategories, ...newCategoryData]);
  };

  const setFiscalStates = (product) => {
    const {
      cfop,
      icms_situacao_tributaria,
      pis_situacao_tributaria,
      cofins_situacao_tributaria,
      icms_origem,
      icms_aliquota,
      pis_aliquota,
      cofins_aliquota,
    } = product;

    if (cfop) {
      const foundCfop = cfopFrame.find((frameItem) => frameItem.value === cfop);
      setCfopCode(foundCfop ?? cfopFrame[0]);
    }

    if (icms_situacao_tributaria) {
      const foundIcmsSituacaoTributaria = icms_situacao_tributaria_frame.find(
        (frameItem) => frameItem.value === icms_situacao_tributaria
      );
      setIcmsSituacaoTributariaState(
        foundIcmsSituacaoTributaria ?? icms_situacao_tributaria_frame[0]
      );
    }

    if (icms_origem) {
      const foundIcmsOrigem = icms_origem_frame.find(
        (frameItem) => frameItem.value === icms_origem
      );
      setIcmsOrigemState(foundIcmsOrigem ?? icms_origem_frame[0]);
    }

    if (icms_aliquota) {
      setIcmsValue(icms_aliquota);
    }

    if (pis_situacao_tributaria) {
      const foundPisSituacaoTributaria = pis_situacao_tributaria_frame.find(
        (frameItem) => frameItem.value === pis_situacao_tributaria
      );
      setPisSituacaoTributariaState(
        foundPisSituacaoTributaria ?? pis_situacao_tributaria_frame[0]
      );
    }

    if (pis_aliquota) {
      setPisValue(pis_aliquota);
    }

    if (cofins_situacao_tributaria) {
      const foundCofinsSituacaoTributaria =
        cofins_situacao_tributaria_frame.find(
          (frameItem) => frameItem.value === cofins_situacao_tributaria
        );
      setCofinsSituacaoTributariaState(
        foundCofinsSituacaoTributaria ?? cofins_situacao_tributaria_frame[0]
      );
    }

    if (cofins_aliquota) {
      setCofinsValue(cofins_aliquota);
    }
  };

  const searchComplements = async (event) => {
    try {
      event.preventDefault();
      setLoading(true);

      setPage(1);

      const params = { page: page, title: complementTitle };
      const { data } = await getComplements(params);
      setComplementsList(data);
    } catch (e) {
      setErrorMessage(e);
      setOpenErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const addComplementsToDelete = () => {
    const complementsToAdd = complementsList.filter(
      (complement) => !complement.checked && complement.existed
    );
    setComplementsListToDelete((prevList) => [
      ...prevList,
      ...complementsToAdd,
    ]);
  };

  const handleIsChecked = (value, index) => {
    const _complementsList = complementsList;
    _complementsList[index].checked = value;
    setComplementsList(_complementsList);
    addComplementsToDelete();
  };

  const handleIsCategoryChecked = (value, index) => {
    const _categoriesList = categoriesList;
    _categoriesList[index].checked = value;
    setCategoriesList(_categoriesList);
    addCategoriesToDelete();
  };

  const addCategoriesToDelete = () => {
    const categoriesToAdd = categoriesList.filter(
      (category) => !category.checked && category.existed
    );
    setCategoriesListToDelete((prevList) => [...prevList, ...categoriesToAdd]);
  };

  const handleSelectedUnit = (event) => {
    setUnit(event.target.value);
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    const isValid = validateString(title);

    if (!isValid) {
      setIsTitleValid(validateString(title));
      setErrorMessage(ERROR_REQUIRED_FIELDS);
      setOpenErrorAlert(true);
      return;
    }

    // Convert the formatted price string to float before submitting
    const priceAsFloat = convertPriceToFloat(price);

    createProduct(isEditing, {
      product_id: isEditing ? product.product_id : uuidv4(),
      title: title,
      fiscal_code: fiscal_code,
      cfop: cfop_code.value,
      icms_situacao_tributaria: icmsSituacaoTributariaState.value,
      icms_origem: icmsOrigemState.value,
      pis_situacao_tributaria: pisSituacaoTributariaState.value,
      cofins_situacao_tributaria: cofinsSituacaoTributariaState.value,
      icms_aliquota: icmsValue,
      pis_aliquota: pisValue,
      cofins_aliquota: cofinsValue,
      ifood_pos_code: ifoodExternalCode,
      unit: unit,
      price: priceAsFloat.toFixed(2),
      width: parseFloat(width || 0).toFixed(2),
      length: parseFloat(length || 0).toFixed(2),
      height: parseFloat(height || 0).toFixed(2),
      weight: parseFloat(weight || 0).toFixed(2),
      available: available,
      deleted_at: null,
      complements: complementsList.filter(
        (complement) => complement.checked && !complement.existed
      ),
      categories: categoriesList.filter(
        (category) => category.checked && !category.existed
      ),
      complementsToDelete: complementsListToDelete,
      categoriesListToDelete: categoriesListToDelete,
    });

    closeModal();
  };

  if (loading) {
    return (
      <AnimatedModal isOpen={show} onClose={closeModal} zIndex={5000}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <CircularProgress />
        </Box>
      </AnimatedModal>
    );
  }

  return (
    <AnimatedModal isOpen={show} onClose={closeModal} zIndex={5000}>
      <ModalHeader
        title={isEditing ? product.title : "Novo Produto"}
        icon={InventoryIcon}
        onClose={closeModal}
      />

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            "& .MuiTab-root": {
              minHeight: "64px",
              fontWeight: 600,
            },
          }}
        >
          <Tab
            icon={<InventoryIcon />}
            label="Informações"
            id="product-tab-0"
            aria-controls="product-tabpanel-0"
          />
          <Tab
            icon={<ReceiptIcon />}
            label="Fiscal"
            id="product-tab-1"
            aria-controls="product-tabpanel-1"
          />
          <Tab
            icon={<CategoryIcon />}
            label="Categorias"
            id="product-tab-2"
            aria-controls="product-tabpanel-2"
          />
          <Tab
            icon={<ExtensionIcon />}
            label="Complementos"
            id="product-tab-3"
            aria-controls="product-tabpanel-3"
          />
        </Tabs>
      </Box>

      <ModalContent padding={0}>
        {/* Basic Info Tab */}
        <TabPanel value={tabValue} index={0}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.light, 0.05),
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    variant="outlined"
                    id="new_product_name"
                    name="title"
                    label="Nome do Produto"
                    fullWidth
                    required
                    error={!isTitleValid && submitted}
                    value={title}
                    helperText={
                      !isTitleValid && submitted ? STRING_NOT_NULL_HELPER : ""
                    }
                    onChange={(event) => {
                      setTitle(event.target.value);
                    }}
                    InputProps={{
                      sx: { borderRadius: 2 },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MoneyTextField
                    id="product-price"
                    label="Preço"
                    value={price}
                    setValue={setPrice}
                    required={true}
                    fullWidth={true}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider>
                    <Chip
                      label="Unidade de Medida"
                      color="primary"
                      variant="outlined"
                    />
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      aria-labelledby="product-units-group"
                      name="product-units"
                      value={unit}
                      onChange={handleSelectedUnit}
                    >
                      <FormControlLabel
                        value="UN"
                        control={
                          <Radio
                            sx={{
                              "&.Mui-checked": {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                        }
                        label="Unidade"
                      />
                      <FormControlLabel
                        value="KG"
                        control={
                          <Radio
                            sx={{
                              "&.Mui-checked": {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                        }
                        label="Quilograma (Kg)"
                      />
                      <FormControlLabel
                        value="L"
                        control={
                          <Radio
                            sx={{
                              "&.Mui-checked": {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                        }
                        label="Litro (L)"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Divider>
                    <Chip
                      label="Disponibilidade"
                      color="primary"
                      variant="outlined"
                    />
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={available}
                        onChange={(e) => setAvailable(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body1">
                        {available
                          ? "Produto disponível"
                          : "Produto indisponível"}
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </motion.div>
        </TabPanel>

        {/* Fiscal Tab */}
        <TabPanel value={tabValue} index={1}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <ItemFiscalDataForm
              fiscalCode={fiscal_code}
              setFiscalCode={setFiscalCode}
              cofinsSituacaoTributariaState={cofinsSituacaoTributariaState}
              setCofinsSituacaoTributariaState={
                setCofinsSituacaoTributariaState
              }
              cofinsValue={cofinsValue}
              setCofinsValue={setCofinsValue}
              pisSituacaoTributariaState={pisSituacaoTributariaState}
              setPisSituacaoTributariaState={setPisSituacaoTributariaState}
              pisValue={pisValue}
              setPisValue={setPisValue}
              icmsOrigemState={icmsOrigemState}
              setIcmsOrigemState={setIcmsOrigemState}
              icmsSituacaoTributariaState={icmsSituacaoTributariaState}
              setIcmsSituacaoTributariaState={setIcmsSituacaoTributariaState}
              icmsValue={icmsValue}
              setIcmsValue={setIcmsValue}
              cfopCode={cfop_code}
              setCfopCode={setCfopCode}
              ifoodExternalCode={ifoodExternalCode}
              setIfoodExternalCode={setIfoodExternalCode}
            />
          </motion.div>
        </TabPanel>

        {/* Categories Tab */}
        <TabPanel value={tabValue} index={2}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
                maxHeight: "500px",
                overflow: "auto",
              }}
            >
              {categoriesList.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    opacity: 0.7,
                  }}
                >
                  <CategoryIcon sx={{ fontSize: 48, opacity: 0.6, mb: 2 }} />
                  <Typography variant="body1">
                    Nenhuma categoria disponível
                  </Typography>
                </Box>
              ) : (
                categoriesList.map((category, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Box
                      sx={{
                        mb: 1,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.light, 0.1),
                          transform: "translateX(5px)",
                        },
                      }}
                    >
                      <CategoryRowWithCheckbox
                        category={category}
                        index={index}
                        isSelected={category.checked}
                        setIsSelected={handleIsCategoryChecked}
                      />
                      <Divider />
                    </Box>
                  </motion.div>
                ))
              )}
            </Box>
          </motion.div>
        </TabPanel>

        {/* Complements Tab */}
        <TabPanel value={tabValue} index={3}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Box
              component="form"
              noValidate
              autoComplete="off"
              onSubmit={searchComplements}
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
              }}
            >
              <FormControl
                sx={{ flex: 1 }}
                variant="outlined"
                onSubmit={searchComplements}
              >
                <InputLabel htmlFor="search-complements-input">
                  Pesquisar complementos
                </InputLabel>
                <OutlinedInput
                  id="search-complements-input"
                  type="text"
                  label="Pesquisar complementos"
                  onChange={(event) => {
                    setComplementTitle(event.target.value);
                  }}
                  sx={{ borderRadius: 2 }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="search complements"
                        onClick={searchComplements}
                        edge="end"
                        type="submit"
                        color="primary"
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
                maxHeight: "500px",
                overflow: "auto",
              }}
            >
              {complementsList.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    opacity: 0.7,
                  }}
                >
                  <ExtensionIcon sx={{ fontSize: 48, opacity: 0.6, mb: 2 }} />
                  <Typography variant="body1">
                    Nenhum complemento encontrado
                  </Typography>
                </Box>
              ) : (
                complementsList.map((complement, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Box
                      sx={{
                        mb: 1,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.light, 0.1),
                          transform: "translateX(5px)",
                        },
                      }}
                    >
                      <ItemRowWithCheckbox
                        complement={complement}
                        itemIndex={index}
                        isSelected={complement.checked}
                        setIsSelected={handleIsChecked}
                      />
                      <Divider />
                    </Box>
                  </motion.div>
                ))
              )}
            </Box>
          </motion.div>
        </TabPanel>
      </ModalContent>

      <ModalFooter>
        <Box sx={{ width: "100%" }}>
          <Collapse in={openErrorAlert}>
            <Alert
              variant="filled"
              severity="warning"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setOpenErrorAlert(false);
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{ mb: 2 }}
            >
              {errorMessage}
            </Alert>
          </Collapse>

          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Button
              variant="outlined"
              onClick={closeModal}
              sx={{ borderRadius: 2 }}
            >
              Cancelar
            </Button>

            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                startIcon={isEditing ? <CheckCircleIcon /> : <AddIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                }}
              >
                {isEditing ? "Salvar Alterações" : "Criar Produto"}
              </Button>
            </motion.div>
          </Stack>
        </Box>
      </ModalFooter>
    </AnimatedModal>
  );
}
